import { useState } from 'react';

// 목록 화면 공용 — 검색어(공백으로 나눈 낱말이 모두 들어 있는 항목, 대소문자 무시) + 페이지 나눔.
// 목록은 서버에서 통째로 받아(최대 수백 건) 화면에서 거른다. 필터는 호출하는 쪽이 items 를 미리 걸러서 넘긴다.

export const PAGE_SIZES = [20, 50, 100] as const;

export function useListView<T>(items: T[], text: (item: T) => string) {
  const [query, setQueryRaw] = useState('');
  const [page, setPage] = useState(1);
  const [size, setSizeRaw] = useState<number>(20);
  const words = query.trim().toLowerCase().split(/\s+/).filter(Boolean);
  // 수백 건이라 렌더마다 거른다(메모이즈 불필요)
  const filtered = words.length
    ? items.filter((it) => {
        const t = text(it).toLowerCase();
        return words.every((w) => t.includes(w));
      })
    : items;
  const pages = Math.max(1, Math.ceil(filtered.length / size));
  const current = Math.min(page, pages);
  return {
    query,
    setQuery: (q: string) => {
      setQueryRaw(q);
      setPage(1);
    },
    size,
    setSize: (n: number) => {
      setSizeRaw(n);
      setPage(1);
    },
    page: current,
    setPage,
    pages,
    total: filtered.length,
    pageItems: filtered.slice((current - 1) * size, current * size),
    /** 필터를 바꿀 때 1쪽으로 */
    resetPage: () => setPage(1),
  };
}

const inputCls =
  'h-9 px-3 border border-neutral-300 rounded-md text-sm bg-surface focus:outline-none focus:ring-2 focus:ring-brand-400';

export function SearchBox({
  value,
  onChange,
  placeholder,
}: { value: string; onChange: (v: string) => void; placeholder: string }) {
  return (
    <input
      type="search"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      aria-label="검색"
      className={`${inputCls} w-72 max-w-full`}
    />
  );
}

export function FilterSelect<V extends string>({
  label,
  value,
  onChange,
  options,
}: {
  label: string;
  value: V;
  onChange: (v: V) => void;
  options: { value: V; label: string }[];
}) {
  return (
    <select
      aria-label={label}
      value={value}
      onChange={(e) => onChange(e.target.value as V)}
      className={inputCls}
    >
      {options.map((o) => (
        <option key={o.value} value={o.value}>
          {o.label}
        </option>
      ))}
    </select>
  );
}

/** 검색창 줄 — [검색] [필터…] 건수 ······ [보기 개수] */
export function ListToolbar({
  total,
  unit,
  searching,
  size,
  onSize,
  children,
}: {
  total: number;
  unit: string;
  searching: boolean;
  size: number;
  onSize: (n: number) => void;
  children: React.ReactNode;
}) {
  return (
    <div className="flex flex-wrap items-center gap-2 mb-3">
      {children}
      <span className="text-sm text-neutral-500 tabular-nums ml-1">
        {searching ? '검색 결과 ' : '총 '}
        {total.toLocaleString('ko-KR')}
        {unit}
      </span>
      <select
        aria-label="보기 개수"
        value={size}
        onChange={(e) => onSize(Number(e.target.value))}
        className={`${inputCls} ml-auto`}
      >
        {PAGE_SIZES.map((n) => (
          <option key={n} value={n}>
            {n}개씩
          </option>
        ))}
      </select>
    </div>
  );
}

/** 페이지 번호 — 1쪽뿐이면 그리지 않는다 */
export function Pager({
  page,
  pages,
  onPage,
}: { page: number; pages: number; onPage: (n: number) => void }) {
  if (pages <= 1) return null;
  const nums = Array.from({ length: pages }, (_, i) => i + 1).filter(
    (n) => n === 1 || n === pages || Math.abs(n - page) <= 2,
  );
  const btn = (on: boolean) =>
    `min-w-9 h-9 px-2 rounded-md text-sm ${on ? 'bg-brand-600 text-white' : 'border border-neutral-300 hover:bg-neutral-50 disabled:opacity-40'}`;
  return (
    <nav aria-label="페이지" className="flex items-center justify-center gap-1 mt-4">
      <button
        type="button"
        className={btn(false)}
        disabled={page <= 1}
        onClick={() => onPage(page - 1)}
        aria-label="이전 페이지"
      >
        ‹
      </button>
      {nums.map((n, i) => (
        <span key={n} className="flex items-center gap-1">
          {i > 0 && n - (nums[i - 1] ?? 0) > 1 && <span className="px-1 text-neutral-400">…</span>}
          <button
            type="button"
            className={btn(n === page)}
            aria-current={n === page ? 'page' : undefined}
            onClick={() => onPage(n)}
          >
            {n}
          </button>
        </span>
      ))}
      <button
        type="button"
        className={btn(false)}
        disabled={page >= pages}
        onClick={() => onPage(page + 1)}
        aria-label="다음 페이지"
      >
        ›
      </button>
    </nav>
  );
}
